import { useCallback, useContext, useMemo } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { usePreference, preferenceNames } from '@bingads-webui-campaign-react/pmax-lite-data';
import { useWizardService, getFlowFactors, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  initialBrandExclusionsViewModel,
  loadCampaign,
  loadConversionGoalDetails,
  saveCampaignAndLinkedData,
  loadBundleCountries,
  modelToViewModelForCampaign,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { readLocationCriterions, readNewCustomerAcquisitionGoals } from '@bingads-webui-campaign-react/perf-max-data';
import { MAX_CONVERSIONS } from '@bingads-webui-campaign-react/bid-strategy-component';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useBudgetSuggestionRepository, MODE } from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import { LogTargetCpaCheckOnSave } from '@bingads-webui-campaign-react/target-cpa-warning-banner';
import { useLocalizeBudget } from './utils/use-budget-localized';
import { FLOWS } from '../step-constants';

export const roasMtToUI = roas => (_.isNumber(roas) ? roas * 100 : null);
export const roasUiToMt = roas => (_.isNumber(roas) ? (roas / 100).toFixed(4) : null);

export const useBudgetStepRepository = () => {
  const { localizeBudget } = useLocalizeBudget();
  const [{ value: currencyPreference }] = usePreference(preferenceNames.SelectedCurrency);
  const { state, send } = useWizardService();
  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions, CurrentAccount: { CurrencyCode } } = serverContext || {};

  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const {
    EnableBudgetSuggestion,
    NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
  } = permissions.dynamic;

  const { flow } = getFlowFactors(state);

  const shouldShowBudgetSuggestion = useMemo(() => EnableBudgetSuggestion && flow === FLOWS.PERFMAX_WIZARD, [EnableBudgetSuggestion, flow]);

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const { campaign: savedCampaign, assetGroup, diagnostics } = state.context.wizardData;

  const {
    loadBudget,
    loadBudgetPartial,
    saveBudget,
    fetchCustomBudgetPerformanceData,
  } = useBudgetSuggestionRepository({
    campaignId: savedCampaign.Id,
    mode: MODE.CREATION,
    campaignType: CampaignTypes.PerformanceMax,
    currency: CurrencyCode,
    campaign: savedCampaign,
    assetGroup,
  });

  const modelToViewModel = useCallback((campaign) => {
    const budgetAmount = _.get(campaign, ['Budget', 'Amount']);
    const bidStrategy = _.get(campaign, ['BiddingScheme', 'Type']);
    const cpa = _.get(campaign, ['BiddingScheme', 'TargetCpa']);
    const roas = parseFloat(_.get(campaign, ['BiddingScheme', 'TargetRoas'])) || null;
    const language = _.get(campaign, ['Languages', 0]);
    const isInitialAdditionalDetailsChecked = _.isNumber(cpa) || _.isNumber(roas);

    return {
      budgetAmount: localizeBudget(budgetAmount, 'USD'),
      bidStrategy,
      currency: currencyPreference,
      cpa,
      roas: roasMtToUI(roas),
      language,
      isInitialAdditionalDetailsChecked,
      isMarketingMetricValid: true,
    };
  }, [currencyPreference, localizeBudget]);

  const load = useCallback(() => {
    const { Id: campaignId } = savedCampaign || {};

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId), // This is necessary to read 'CampaignConversionGoal' from MT. The current conversion is coupled with the value.
      conversionGoalDetails: loadConversionGoalDetails({ cid, aid }),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoals: readNewCustomerAcquisitionGoals(cid, aid) }),
    };

    return Promise.props(loadPromises).then(({
      campaign,
      locationTargets,
      bundleCountriesAndLocationCriterions,
      conversionGoalDetails,
      newCustomerAcquisitionGoals,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: { ...campaign, Status: savedCampaign.status },
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoalsModel: newCustomerAcquisitionGoals ? newCustomerAcquisitionGoals.value : [] }),
      });

      return {
        campaign: campaignData.data,
        assetGroup,
        conversionGoalDetails,
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        ...bundleCountriesAndLocationCriterions,
        ...modelToViewModel(campaign),
        fetchCustomBudgetPerformanceData,
        loadBudgetPartial,
      };
    })
      .then((result) => {
        if (shouldShowBudgetSuggestion) {
          return loadBudget()
            .then(budgetSuggestionData => ({ ...result, budgetSuggestionData }))
            .catch((err) => {
              activity.error('[PmaxNorthStarWizard] - failed to load budget suggestion data', err);
              return { ...result, budgetSuggestionData: null };
            });
        }
        return Promise.resolve(result);
      });
  }, [savedCampaign, cid, aid, serverContext, isNewCustomerAcquisitionEnabled, assetGroup, modelToViewModel, fetchCustomBudgetPerformanceData, loadBudgetPartial, shouldShowBudgetSuggestion, loadBudget, activity]);

  const save = useCallback(({ data }) => {
    const {
      campaign: updatedCampaign,
      budgetAmount,
      bidStrategy,
      cpa,
      roas,
      isBudgetValid,
      isMarketingMetricValid,
      conversionGoalDetails,
      budgetTier,
      bundleCountries,
      bundleLocationCriterions,
      budgetSuggestionData,
    } = data || {};

    if (!isBudgetValid || !isMarketingMetricValid) {
      return Promise.reject();
    }

    const newCampaign = {
      ...updatedCampaign,
      BiddingScheme: {
        Type: bidStrategy,
        ...(
          bidStrategy === MAX_CONVERSIONS.type
            ? { TargetCpa: cpa }
            : { TargetRoas: roasUiToMt(roas) }
        ),
      },
      Budget: {
        Amount: i18n.parseDecimal(budgetAmount),
        Type: 'DailyStandard',
      },
    };

    const savePromises = {
      campaign: saveCampaignAndLinkedData({
        campaign: { data: newCampaign },
        originalCampaign: { data: savedCampaign },
        brandExclusions: initialBrandExclusionsViewModel,
        bundleCountries,
        bundleLocationCriterions,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateCampaign'),
      }),
    };

    if (shouldShowBudgetSuggestion) {
      // we call save budget from the hook, since it has the necessary metrics logging to report.
      savePromises.budgetSuggestion = saveBudget({ campaign: newCampaign, budgetAmount, budgetTier });
    }

    return Promise.props(savePromises).then(({
      campaign: campaignResults,
      budgetSuggestion: budgetSuggestionResults,
    }) => {
      activity.trace('[PmaxNorthStarWizard] - campaign settings updated in budget step');
      activity.signal('[PmaxNorthStarWizard] - campaign settings updated in budget step');

      const { campaign } = campaignResults;

      LogTargetCpaCheckOnSave(i18n, permissions, campaign?.data?.Budget?.Amount, campaign?.data?.BiddingScheme.TargetCpa, activity, 'PmaxNorthStarWizard');


      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        campaign: {
          ...campaign.data,
          locationTargets: updatedCampaign.locationTargets,
        },
        diagnostics: {
          ...diagnostics,
          budgetSuggestionData,
        },
      });

      if (budgetSuggestionResults) {
        activity.trace('[PmaxNorthStarWizard] - budget suggestion adopted in budget step');
      }

      return {
        campaign: campaign.data,
        conversionGoalDetails,
        ...modelToViewModel(campaign.data),
      };
    }).catch((errorData) => {
      activity.trace('[PmaxNorthStarWizard] - campaign settings update failed in budget step');

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ error: { ...errorData } });
    });
  }, [i18n, savedCampaign, scenarioContext, shouldShowBudgetSuggestion, saveBudget, activity, permissions, send, diagnostics, modelToViewModel]);

  return {
    load,
    save,
  };
};
